import {MutationFunction, useMutation, useQuery, UseQueryOptions} from '@tanstack/react-query';
import {
  createTranslation,
  deleteTranslation,
  getAITranslation,
  getTranslations,
  updateTranslation,
  UpsertEntityTranslationFn,
  upsertMethodInfoTranslation,
  upsertMethodMaterialTranslation,
  upsertMethodStepTranslation,
  upsertMethodTranslation,
  upsertWorkshopInfoTranslation,
  upsertWorkshopTranslation,
} from 'services/axios/modules';
import {queryClient} from '..';

export const useTranlsations = (translationKeys?: string[], options: UseQueryOptions<ToolboxObject.Translation[]> = {}) => {
  return useQuery<ToolboxObject.Translation[]>(
    translationKeys ? ['translations', {translationKeys}] : ['translations'],
    () => {
      return getTranslations(translationKeys);
    },
    {staleTime: 3600000, cacheTime: 3600000, ...options}
  );
};

export const useAITranlsation = () => {
  return useMutation({
    mutationFn: (data: {message: string; lang: string}) => {
      return getAITranslation(data);
    },
  });
};

export const useUpdateTranlsation = () => {
  return useMutation({
    mutationFn: (data: ToolboxObject.Translation) => {
      const {id, ...rest} = data;
      return updateTranslation(id, rest);
    },
    onSuccess: async (updatedTranslation: ToolboxObject.Translation) => {
      queryClient.setQueryData(['translations'], (translations: ToolboxObject.Translation[] = []) => {
        return translations.map((t) => (t.id === updatedTranslation.id ? updatedTranslation : t));
      });
      queryClient.invalidateQueries(['methods']);
    },
  });
};

export const useCreateTranlsation = () => {
  return useMutation({
    mutationFn: (data: Omit<ToolboxObject.Translation, 'id'>) => {
      return createTranslation(data);
    },
    onSuccess: async (createdTranslation: ToolboxObject.Translation) => {
      queryClient.setQueryData(['translations'], (translations: ToolboxObject.Translation[] = []) => {
        return [createdTranslation, ...translations];
      });
      queryClient.invalidateQueries(['methods']);
    },
  });
};

export const useDeleteTranlsation = () => {
  return useMutation({
    mutationFn: (id: number) => {
      return deleteTranslation(id);
    },
    onSuccess: async (deletedId: number) => {
      queryClient.setQueryData(['translations'], (translations: ToolboxObject.Translation[] = []) => {
        return translations.filter((t) => t.id !== deletedId);
      });
      queryClient.invalidateQueries(['methods']);
    },
  });
};

export type UpsertEntityTranslationMutationFunction = MutationFunction<ToolboxObject.EntityTranslation, ToolboxObject.UpsertTranslationDto>;

export const useUpsertEntityTranlsation = (entityId: number, createFn: UpsertEntityTranslationFn) => {
  const mutationFn: UpsertEntityTranslationMutationFunction = async (data) => {
    return createFn(entityId, data);
  };
  return useMutation({
    mutationFn,
  });
};

// Method translations
export const useUpsertMethodTranlsation = (methodId: number) => {
  return useUpsertEntityTranlsation(methodId, upsertMethodTranslation);
};

export const useUpsertMethodInfoTranlsation = (methodId: number) => {
  return useUpsertEntityTranlsation(methodId, upsertMethodInfoTranslation);
};

export const useUpsertMethodMaterialTranlsation = (methodId: number) => {
  return useUpsertEntityTranlsation(methodId, upsertMethodMaterialTranslation);
};

export const useUpsertMethodStepTranlsation = (methodId: number) => {
  return useUpsertEntityTranlsation(methodId, upsertMethodStepTranslation);
};

// Workshop translations
export const useUpsertWorkshopTranlsation = (workshopId: number) => {
  return useUpsertEntityTranlsation(workshopId, upsertWorkshopTranslation);
};

export const useUpsertWorkshopInfoTranlsation = (workshopId: number) => {
  return useUpsertEntityTranlsation(workshopId, upsertWorkshopInfoTranslation);
};
